/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BrandKit } from "../models/BrandKit";
import type { BrandKitUpdate } from "../models/BrandKitUpdate";
import type { MessageTemplateDetail } from "../models/MessageTemplateDetail";
import type { MessageTemplateSummary } from "../models/MessageTemplateSummary";
import type { MessageTemplateUpdate } from "../models/MessageTemplateUpdate";
import type { QueueDecision } from "../models/QueueDecision";
import type { QueueDecisionResponse } from "../models/QueueDecisionResponse";
import type { QueuedSend } from "../models/QueuedSend";
import type { TemplateCatalog } from "../models/TemplateCatalog";
import type { TemplatePreview } from "../models/TemplatePreview";
import type { TemplatePreviewRequest } from "../models/TemplatePreviewRequest";
import type { TemplateSendRequest } from "../models/TemplateSendRequest";
import type { TemplateSendResponse } from "../models/TemplateSendResponse";
import type { TemplateTestRequest } from "../models/TemplateTestRequest";
import type { TemplateTestResponse } from "../models/TemplateTestResponse";
import type { TriggerCreate } from "../models/TriggerCreate";
import type { TriggerDraft } from "../models/TriggerDraft";
import type { TriggerEnableRequest } from "../models/TriggerEnableRequest";
import type { TriggerReach } from "../models/TriggerReach";
import type { TriggerResponse } from "../models/TriggerResponse";
import type { TriggerUpdate } from "../models/TriggerUpdate";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class MessageTemplatesService {
  /**
   * Get Message Templates
   * Every template of the organization, with its triggers on and its counts.
   * @returns MessageTemplateSummary Successful Response
   * @throws ApiError
   */
  public static getMessageTemplates({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<Array<MessageTemplateSummary>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/message_templates/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Message Template
   * A new template with a free name and starter wording.
   * @returns MessageTemplateDetail Successful Response
   * @throws ApiError
   */
  public static createMessageTemplate({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<MessageTemplateDetail> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Message Template Catalog
   * The variables a template can use, and the triggers and their choices.
   * @returns TemplateCatalog Successful Response
   * @throws ApiError
   */
  public static getMessageTemplateCatalog({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<TemplateCatalog> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/message_templates/organizations/{org_id}/catalog",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Message Template Brand
   * The logo, colors and font of template emails, and the sending hours.
   * @returns BrandKit Successful Response
   * @throws ApiError
   */
  public static getMessageTemplateBrand({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<BrandKit> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/message_templates/organizations/{org_id}/brand",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Message Template Brand
   * Save the brand kit. It never changes the booking pages. The logo is
   * uploaded as an EMAIL_LOGO image.
   * @returns BrandKit Successful Response
   * @throws ApiError
   */
  public static updateMessageTemplateBrand({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: BrandKitUpdate;
  }): CancelablePromise<BrandKit> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/message_templates/organizations/{org_id}/brand",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Remove Message Template Brand Logo
   * Take the logo off template emails. The booking pages keep theirs.
   * @returns BrandKit Successful Response
   * @throws ApiError
   */
  public static removeMessageTemplateBrandLogo({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<BrandKit> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/message_templates/organizations/{org_id}/brand/logo",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        403: `Forbidden`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Preview Message Template
   * The wording filled in for one person, as they would get it, and whose
   * message would have details left blank. Nothing is sent or made.
   * @returns TemplatePreview Successful Response
   * @throws ApiError
   */
  public static previewMessageTemplate({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: TemplatePreviewRequest;
  }): CancelablePromise<TemplatePreview> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/organizations/{org_id}/preview",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Message Template Queue
   * The triggers' messages waiting for approval (oldest first), or the
   * latest ones with other statuses: status can be given more than once,
   * like skipped and dismissed together.
   * @returns QueuedSend Successful Response
   * @throws ApiError
   */
  public static getMessageTemplateQueue({
    orgId,
    status,
  }: {
    orgId: number;
    status?: Array<
      "waiting" | "queued" | "sent" | "failed" | "skipped" | "dismissed"
    >;
  }): CancelablePromise<Array<QueuedSend>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/message_templates/organizations/{org_id}/queue",
      path: {
        org_id: orgId,
      },
      query: {
        status: status,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Decide Message Template Queue
   * Approve or dismiss waiting messages, one at a time or all at once.
   * Approving a failed message tries it again.
   * @returns QueueDecisionResponse Successful Response
   * @throws ApiError
   */
  public static decideMessageTemplateQueue({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: QueueDecision;
  }): CancelablePromise<QueueDecisionResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/organizations/{org_id}/queue",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Message Template Trigger
   * Change a trigger. While it is on, only its approval switch changes.
   * @returns TriggerResponse Successful Response
   * @throws ApiError
   */
  public static updateMessageTemplateTrigger({
    triggerId,
    requestBody,
  }: {
    triggerId: number;
    requestBody: TriggerUpdate;
  }): CancelablePromise<TriggerResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/message_templates/triggers/{trigger_id}",
      path: {
        trigger_id: triggerId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Delete Message Template Trigger
   * Delete a trigger. Its messages that haven't gone out are dismissed.
   * @returns TriggerResponse Successful Response
   * @throws ApiError
   */
  public static deleteMessageTemplateTrigger({
    triggerId,
  }: {
    triggerId: number;
  }): CancelablePromise<TriggerResponse> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/message_templates/triggers/{trigger_id}",
      path: {
        trigger_id: triggerId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Message Template Trigger Reach
   * Who the trigger would message if it were turned on now, and one of
   * their messages, filled in. Look at this before turning it on.
   * @returns TriggerReach Successful Response
   * @throws ApiError
   */
  public static getMessageTemplateTriggerReach({
    triggerId,
  }: {
    triggerId: number;
  }): CancelablePromise<TriggerReach> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/message_templates/triggers/{trigger_id}/reach",
      path: {
        trigger_id: triggerId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Enable Message Template Trigger
   * Turn a trigger on, after looking at who it reaches (seen_count). From
   * then on it sends each message that comes due, also about a meeting or
   * form from before; catch_up asks to send once to those who already match
   * too.
   * @returns TriggerResponse Successful Response
   * @throws ApiError
   */
  public static enableMessageTemplateTrigger({
    triggerId,
    requestBody,
  }: {
    triggerId: number;
    requestBody: TriggerEnableRequest;
  }): CancelablePromise<TriggerResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/triggers/{trigger_id}/enable",
      path: {
        trigger_id: triggerId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Disable Message Template Trigger
   * Turn a trigger off. Its messages that haven't gone out are dismissed.
   * @returns TriggerResponse Successful Response
   * @throws ApiError
   */
  public static disableMessageTemplateTrigger({
    triggerId,
  }: {
    triggerId: number;
  }): CancelablePromise<TriggerResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/triggers/{trigger_id}/disable",
      path: {
        trigger_id: triggerId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Message Template
   * A template, its wording and its triggers, for the editor.
   * @returns MessageTemplateDetail Successful Response
   * @throws ApiError
   */
  public static getMessageTemplate({
    templateId,
  }: {
    templateId: number;
  }): CancelablePromise<MessageTemplateDetail> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/message_templates/{template_id}",
      path: {
        template_id: templateId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Message Template
   * Save the editor. The last save wins.
   * @returns MessageTemplateDetail Successful Response
   * @throws ApiError
   */
  public static updateMessageTemplate({
    templateId,
    requestBody,
  }: {
    templateId: number;
    requestBody: MessageTemplateUpdate;
  }): CancelablePromise<MessageTemplateDetail> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/message_templates/{template_id}",
      path: {
        template_id: templateId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Delete Message Template
   * Delete a template: its triggers turn off and what they queued is
   * dismissed. What it sent stays in Communication.
   * @returns MessageTemplateSummary Successful Response
   * @throws ApiError
   */
  public static deleteMessageTemplate({
    templateId,
  }: {
    templateId: number;
  }): CancelablePromise<MessageTemplateSummary> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/message_templates/{template_id}",
      path: {
        template_id: templateId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Duplicate Message Template
   * 'Copy of <name>' with the same wording and triggers, all turned off.
   * @returns MessageTemplateDetail Successful Response
   * @throws ApiError
   */
  public static duplicateMessageTemplate({
    templateId,
  }: {
    templateId: number;
  }): CancelablePromise<MessageTemplateDetail> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/{template_id}/duplicate",
      path: {
        template_id: templateId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Send Message Template
   * Send the template to people by text and/or email, each filled in for
   * them (wording given here is for this send only). Each message is in
   * Communication.
   * @returns TemplateSendResponse Successful Response
   * @throws ApiError
   */
  public static sendMessageTemplate({
    templateId,
    requestBody,
  }: {
    templateId: number;
    requestBody: TemplateSendRequest;
  }): CancelablePromise<TemplateSendResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/{template_id}/send",
      path: {
        template_id: templateId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Test Message Template
   * Send the template to yourself, filled in with one person's details.
   * @returns TemplateTestResponse Successful Response
   * @throws ApiError
   */
  public static testMessageTemplate({
    templateId,
    requestBody,
  }: {
    templateId: number;
    requestBody: TemplateTestRequest;
  }): CancelablePromise<TemplateTestResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/{template_id}/test",
      path: {
        template_id: templateId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Message Template Trigger
   * A new automatic trigger for the template. It starts off, unless enabled
   * asks to turn it on at once: then it needs seen_count (what the reach
   * preview showed), and with catch_up seen_catch_up_count, and it is added
   * only if who it reaches is unchanged.
   * @returns TriggerResponse Successful Response
   * @throws ApiError
   */
  public static createMessageTemplateTrigger({
    templateId,
    requestBody,
  }: {
    templateId: number;
    requestBody: TriggerCreate;
  }): CancelablePromise<TriggerResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/{template_id}/triggers",
      path: {
        template_id: templateId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Message Template Draft Trigger Reach
   * Who a trigger with these choices would message if it were on now, and
   * one of their messages, filled in: what Add shows before it adds a
   * trigger that is on. Nothing is saved.
   * @returns TriggerReach Successful Response
   * @throws ApiError
   */
  public static getMessageTemplateDraftTriggerReach({
    templateId,
    requestBody,
  }: {
    templateId: number;
    requestBody: TriggerDraft;
  }): CancelablePromise<TriggerReach> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/message_templates/{template_id}/triggers/reach",
      path: {
        template_id: templateId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
